import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { pluralTitleRegExp, SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';
import { isPhone, JOB_STATUSES, Popup } from '@helpers';
import { scale as initialScale } from '@components/frame';
import FinishJobModal from '@v2/components/FinishJobModal';
import { getFinishJobModalPopupConfig } from '@v2/components/FinishJobModal/helpers/popupConfig';
import { useCloseProductionOperationErrorAlert } from '@v2/components/FinishJobModal/hooks/useCloseProductionOperationErrorAlert';
import useIsFinishJobEnabled from '@v2/components/FinishJobModal/hooks/useIsFinishJobEnabled';
import { TRACKING_EVENTS } from '@services/tracking';
import { useHandleMainDetailsScreenNavigation } from '@v2/pages/common/hooks/useHandleMainDetailsScreenNavigation';
import { JobDetailsScreenName } from '@v2/pages/job/navigation/config';
import { JobDetailsScreen } from '@v2/pages/job/common/enums/jobDetails.enum';
import { LevelSubScreen, Perspective } from '@v2/pages/common/enums';
import { selectSingleBatchData } from '@reducers/batch';
import { generateImageURL as generateImageURLAction } from '@reducers/blobStorage';
import { loadJobs } from '@reducers/details';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { useFocusEffect, useRoute } from '@react-navigation/native';
import { NowSubScreen } from '@v2/pages/machines/common/enums';
import { useNavigationCheck } from '@v2/pages/machines/hooks/useNavigationCheck';
import { useDocumentsNavigation } from '@v2/pages/common/hooks/useDocumentsNavigation';
import { ProductionOrderDetailsScreenName } from '@v2/pages/productionOrder/navigation/config';
import { ProductionOrderDetailsScreen } from '@v2/pages/productionOrder/common/enums/productionOrderDetails.enum';
import { useGetMachineByJob } from '@v2/pages/common/hooks/useGetMachineByJob';
import { useGetHeaderConfig } from '@v2/pages/common/hooks/useGetHeaderConfig';
import { JobContentProps } from '@v2/pages/machines/modules/now/components/Job/components/JobContent';
import JobMachinesModal from '@v2/pages/machines/modules/now/components/Job/components/JobMachinesModal';

export interface UseManageJobContentParams extends Pick<JobContentProps, 'jobs' | 'shouldTakeFromBatch' | 'currentJobId' | 'jobDataLoadingForList' | 'ignoreHeaderConfig' | 'scale'> {}

export const useManageJobContent = ({
  jobs,
  shouldTakeFromBatch,
  currentJobId,
  jobDataLoadingForList,
  ignoreHeaderConfig = false,
  scale = initialScale,
}: UseManageJobContentParams) => {
  const route = useRoute();
  const dispatch = useAppDispatch();
  const batch = useSelector(selectSingleBatchData);
  const generateImageURL = useCallback((...args: any[]) => dispatch(generateImageURLAction(...args)), [dispatch]);
  const { safeIsDetailsScreen, safeIsMachinesScreenCheck } = useNavigationCheck();

  const isJobPerspective = route?.params?.perspective === Perspective.JOBS;
  const showList = route?.params?.keepList;

  const headerConfig = useGetHeaderConfig();

  const multipleJobsInHeader = pluralTitleRegExp.test(headerConfig?.job.toString());

  const jobIds = !shouldTakeFromBatch
    ? jobs?.map((j) => j.productionOperationId)
    : batch?.data?.links?.length
    ? batch?.data?.links.map((l) => l.productionOperationId)
    : [];

  const { navigateToMainDetailsScreen } = useHandleMainDetailsScreenNavigation();
  const { safeIsNowSubScreenCheck } = useNavigationCheck();
  const { handleNowScreenNavigation } = useDocumentsNavigation();
  const machineByJobStatus = useGetMachineByJob(jobs?.[0]);
  const isFinishJobEnabled = useIsFinishJobEnabled();

  const [isError, setIsError] = useState<boolean>(false);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [jobDataLoading, setJobDataLoading] = useState<boolean>(true);

  const shouldNotShowLoader = useMemo(() => {
    if (!headerConfig?.job.toString().trim() && !ignoreHeaderConfig) return false;

    if (jobIds?.length !== 1) {
      return !jobDataLoadingForList && !jobDataLoading;
    }

    return jobs && currentJobId && jobs[0]?.productionOperationId === Number(currentJobId);
  }, [jobIds, jobDataLoadingForList, jobDataLoading, currentJobId, jobs, headerConfig?.job, ignoreHeaderConfig]);

  const fetchJobsData = async () => {
    if (isError) {
      setIsError(false);
    }

    try {
      await dispatch(loadJobs({ jobIds }));
    } catch (e) {
      setIsError(true);
    } finally {
      setJobDataLoading(false);
    }
  };

  // No onAlreadyFinished callback — JobAlreadyFinished is handled by the optimistic patch + no-revert path; refetching here hits the lagging search index.
  useCloseProductionOperationErrorAlert();

  const toggleMachines = () => {
    Popup({
      Component: (props) => <JobMachinesModal {...{ ...props, jobIds, job: jobs[0] }} />,
    });
  };

  const linkedMachineId = machineByJobStatus?.machine?.machineId ?? -1;
  // closeProductionOperation doesn't require machineId — don't gate on linkedMachineId or NotStarted jobs (empty machines array) stay un-finishable.
  const showMarkAsFinished = isFinishJobEnabled && jobs?.[0]?.status !== JOB_STATUSES.FINISHED && !isPhone;

  const handleMarkAsFinished = () => {
    if (!jobs?.[0] || !isFinishJobEnabled) return;
    Popup({
      Component: (p: { hide: () => void }) => (
        <FinishJobModal
          hide={p.hide}
          item={jobs[0]}
          machineId={linkedMachineId}
          originScreen={TRACKING_EVENTS.JOB}
        />
      ),
      ...getFinishJobModalPopupConfig(scale),
    });
    // No onFinishSuccess refetch — operationsSearch hits a lagging index; thunk patches details.jobs optimistically.
  };

  useFocusEffect(
    useCallback(() => {
      if (!shouldTakeFromBatch) {
        setJobDataLoading(false);
        return;
      }

      if (shouldTakeFromBatch && jobIds.length) {
        fetchJobsData();
      }
    }, [batch.data?.productionBatchId, shouldTakeFromBatch])
  );

  const currentJob = jobs[0];

  const onJobNumberClick = () => {
    navigateToMainDetailsScreen({
      mainScreen: JobDetailsScreenName,
      subScreen: JobDetailsScreen.JOBS,
      levelSubScreen: LevelSubScreen.GENERAL,
      params: {
        perspective: Perspective.JOBS,
        productionOperationId: jobIds[0],
      },
    });
  };

  const onProductionOrderNumberClick = () => {
    navigateToMainDetailsScreen({
      mainScreen: ProductionOrderDetailsScreenName,
      subScreen: ProductionOrderDetailsScreen.ORDER,
      params: {
        perspective: Perspective.ORDER,
        productionOrderId: jobs[0]?.productionOrderId,
      },
    });
  };

  const onJobImageClick = (selectedImageKey: string) => {
    if (safeIsNowSubScreenCheck) {
      handleNowScreenNavigation({ nowSubScreen: NowSubScreen.JOB, params: { selectedImageKey } });
      return;
    }
    navigateToMainDetailsScreen({
      mainScreen: JobDetailsScreenName,
      subScreen: JobDetailsScreen.JOBS,
      levelSubScreen: LevelSubScreen.DOCUMENTS,
      params: {
        perspective: Perspective.JOBS,
        productionOperationId: jobIds[0],
        selectedImageKey,
      },
    });
  };

  const shouldDisplayJobNumberAsText = (safeIsDetailsScreen && isJobPerspective && !showList) || (!safeIsMachinesScreenCheck && !safeIsDetailsScreen);

  const machineName = machineByJobStatus?.machine?.machineName;

  const borderRadiusConfig = { borderRadius: scale(SCREEN_BORDER_RADIUS) };

  return {
    batch,
    generateImageURL,
    safeIsDetailsScreen,
    safeIsMachinesScreenCheck,
    safeIsNowSubScreenCheck,
    isJobPerspective,
    showList,
    multipleJobsInHeader,
    jobIds,
    machineByJobStatus,
    isError,
    containerWidth,
    setContainerWidth,
    shouldNotShowLoader,
    fetchJobsData,
    toggleMachines,
    currentJob,
    onJobNumberClick,
    onProductionOrderNumberClick,
    onJobImageClick,
    shouldDisplayJobNumberAsText,
    machineName,
    borderRadiusConfig,
    showMarkAsFinished,
    handleMarkAsFinished,
  };
};
